<script lang="ts">
  import { push } from 'svelte-spa-router';
  import { AuthService } from '../../lib/api/auth.service';
  import PasswordInput from '../../components/PasswordInput.svelte';
  import AuthPageLayout from '../../components/auth/AuthPageLayout.svelte';

  export let params: { token?: string } = {};

  let password = '';
  let passwordConfirmation = '';
  let loading = false;
  let success = false;
  let formError = '';

  let passwordError = '';
  let confirmError = '';

  function validatePassword(): boolean {
    passwordError = '';
    confirmError = '';
    formError = '';
    let valid = true;

    if (!password) {
      passwordError = 'Password is required';
      valid = false;
    } else if (password.length < 8) {
      passwordError = 'Password must be at least 8 characters long';
      valid = false;
    }

    if (!passwordConfirmation) {
      confirmError = 'Please confirm your password';
      valid = false;
    } else if (password !== passwordConfirmation) {
      confirmError = 'Passwords do not match';
      valid = false;
    }

    return valid;
  }

  function applyApiValidationErrors(errors: Record<string, string[] | string> | undefined): boolean {
    if (!errors || typeof errors !== 'object') return false;

    const passwordMessages = errors.password;
    const confirmMessages = errors.password_confirmation;

    if (passwordMessages) {
      passwordError = Array.isArray(passwordMessages) ? passwordMessages[0] : passwordMessages;
    }
    if (confirmMessages) {
      confirmError = Array.isArray(confirmMessages) ? confirmMessages[0] : confirmMessages;
    }

    return Boolean(passwordError || confirmError);
  }

  async function handleSubmit() {
    if (!validatePassword()) return;

    if (!params.token) {
      formError = 'Invalid reset link. Please request a new password reset.';
      return;
    }

    loading = true;
    formError = '';

    try {
      const response = await AuthService.updatePasswordViaReset({
        token: params.token,
        password: password,
        password_confirmation: passwordConfirmation,
      });

      if (response.success) {
        success = true;
      } else if (!applyApiValidationErrors(response.errors)) {
        formError = response.message || 'Failed to reset password. The link may have expired.';
      }
    } catch (error: any) {
      console.error('Password reset error:', error);
      formError =
        error?.response?.data?.message || 'Failed to reset password. Please try again.';
    } finally {
      loading = false;
    }
  }
</script>

{#if !params.token}
  <AuthPageLayout title="Invalid reset link" subtitle="This password reset link is not valid.">
    <div class="auth-status-panel">
      <svg
        width="56"
        height="56"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        class="auth-status-icon error"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="15" y1="9" x2="9" y2="15"></line>
        <line x1="9" y1="9" x2="15" y2="15"></line>
      </svg>
      <p>Please request a new password reset link and try again.</p>
      <div class="auth-actions">
        <button type="button" class="auth-btn" on:click={() => push('/forgot-password')}>
          Request new link
        </button>
        <button type="button" class="auth-btn-secondary" on:click={() => push('/login')}>
          Back to sign in
        </button>
      </div>
    </div>
  </AuthPageLayout>
{:else if success}
  <AuthPageLayout title="Password updated" subtitle="You can now sign in with your new password.">
    <div class="auth-status-panel">
      <svg
        width="56"
        height="56"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        class="auth-status-icon success"
        aria-hidden="true"
      >
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
      <p>Your password has been successfully reset.</p>
      <div class="auth-actions">
        <button type="button" class="auth-btn" on:click={() => push('/login')}>
          Go to sign in
        </button>
      </div>
    </div>
  </AuthPageLayout>
{:else}
  <AuthPageLayout title="Reset password" subtitle="Choose a new password for your account.">
    <form on:submit|preventDefault={handleSubmit}>
      {#if formError}
        <div class="auth-form-error" role="alert">{formError}</div>
      {/if}

      <section class="auth-form-section" aria-labelledby="reset-password-heading">
        <h2 id="reset-password-heading" class="auth-form-section-title">New password</h2>
        <div class="auth-form-section-body">
          <div class="auth-password-row">
            <div class="auth-form-group">
              <label for="new-password">
                New password <span class="auth-required" aria-label="required">*</span>
              </label>
              <PasswordInput
                id="new-password"
                bind:value={password}
                required={true}
                disabled={loading}
                placeholder="At least 8 characters"
                autocomplete="new-password"
                ariaInvalid={!!passwordError}
                ariaDescribedby={passwordError ? 'new-password-error' : 'new-password-hint'}
                className={passwordError ? 'error' : ''}
                on:input={() => { passwordError = ''; formError = ''; }}
              />
              <p class="auth-field-hint" id="new-password-hint">Must be at least 8 characters long</p>
              {#if passwordError}
                <p class="auth-field-error" id="new-password-error" role="alert">{passwordError}</p>
              {/if}
            </div>

            <div class="auth-form-group">
              <label for="confirm-password">
                Confirm password <span class="auth-required" aria-label="required">*</span>
              </label>
              <PasswordInput
                id="confirm-password"
                bind:value={passwordConfirmation}
                required={true}
                disabled={loading}
                placeholder="Re-enter password"
                autocomplete="new-password"
                ariaInvalid={!!confirmError}
                ariaDescribedby={confirmError ? 'confirm-password-error' : undefined}
                className={confirmError ? 'error' : ''}
                on:input={() => { confirmError = ''; formError = ''; }}
              />
              {#if confirmError}
                <p class="auth-field-error" id="confirm-password-error" role="alert">{confirmError}</p>
              {/if}
            </div>
          </div>
        </div>
      </section>

      <div class="auth-actions">
        <button
          type="submit"
          class="auth-btn"
          disabled={loading || !password || !passwordConfirmation}
        >
          {loading ? 'Updating...' : 'Reset password'}
        </button>
        <button
          type="button"
          class="auth-btn-secondary"
          on:click={() => push('/login')}
          disabled={loading}
        >
          Back to sign in
        </button>
      </div>
    </form>

    <svelte:fragment slot="footer">
      Remember your password?
      <a href="/login" on:click|preventDefault={() => push('/login')}>Sign in</a>
    </svelte:fragment>
  </AuthPageLayout>
{/if}
